import {
	List,
	ListSubheader,
	ListItemButton,
	ListItemText,
	Chip,
	Skeleton,
} from '@mui/material';
import PropTypes from 'prop-types';

const BADGE_COLORS = {
	red: { bg: 'status.red.bg', color: 'status.red.text' },
	amber: { bg: 'status.amber.bg', color: 'status.amber.text' },
	gray: { bg: 'status.gray.bg', color: 'status.gray.text' },
	vdr: { bg: 'vdr.bg', color: 'vdr.text' },
};

const SidebarSection = ({ label, labelColor, children }) => {
	return (
		<List
			subheader={
				<ListSubheader
					disableGutters
					disableSticky
					sx={{
						display: 'block',
						padding: '0 14px 5px',
						backgroundColor: 'transparent',
						fontSize: '0.75rem',
						fontWeight: 600,
						letterSpacing: '0.08em',
						textTransform: 'uppercase',
						lineHeight: 'normal',
						color: labelColor || 'text.tertiary',
					}}
				>
					{label}
				</ListSubheader>
			}
			disablePadding
			sx={{ mb: '18px' }}
		>
			{children}
		</List>
	);
};

SidebarSection.propTypes = {
	label: PropTypes.string.isRequired,
	labelColor: PropTypes.string,
	children: PropTypes.node.isRequired,
};

const SidebarSectionItem = ({
	name,
	badge,
	badgeColor = 'gray',
	active = false,
	textColor,
	hoverBgColor = 'background.subtle',
	onClick,
	disabled = false,
}) => {
	const badgeStyle = BADGE_COLORS[badgeColor] ?? BADGE_COLORS.gray;
	const itemTextColor =
		textColor || (active ? 'text.primary' : 'text.secondary');

	return (
		<ListItemButton
			disableGutters
			onClick={onClick}
			disabled={disabled}
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				padding: '6px 14px',
				borderLeft: '2px solid',
				borderLeftColor: active ? 'primary.main' : 'transparent',
				backgroundColor: active ? 'background.subtle' : 'transparent',
				'&:hover': {
					backgroundColor: disabled ? 'transparent' : hoverBgColor,
				},
				'&.Mui-disabled': {
					opacity: 0.6,
					'& .MuiListItemText-root': {
						color: itemTextColor,
					},
				},
			}}
		>
			<ListItemText
				primary={name}
				disableTypography
				color={itemTextColor}
				sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
			/>
			{badge !== undefined && (
				<Chip
					label={badge}
					size='small'
					sx={{
						height: '18px',
						fontSize: '10px',
						fontWeight: 600,
						backgroundColor: badgeStyle.bg,
						color: badgeStyle.color,
						'& .MuiChip-label': {
							padding: '0 6px',
						},
					}}
				/>
			)}
		</ListItemButton>
	);
};

SidebarSectionItem.propTypes = {
	name: PropTypes.string.isRequired,
	badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	badgeColor: PropTypes.oneOf(['red', 'amber', 'gray', 'vdr']),
	active: PropTypes.bool,
	textColor: PropTypes.string,
	hoverBgColor: PropTypes.string,
	onClick: PropTypes.func,
	disabled: PropTypes.bool,
};

const SidebarSectionSkeleton = ({ count = 3 }) => {
	return (
		<>
			{Array.from({ length: count }, (_, index) => (
				<ListItemButton
					key={index}
					disableGutters
					disabled
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						padding: '6px 14px',
					}}
				>
					<Skeleton
						variant="text"
						width="70%"
						sx={{ fontSize: '13px', borderRadius: '4px' }}
					/>
				</ListItemButton>
			))}
		</>
	);
};

SidebarSectionSkeleton.propTypes = {
	count: PropTypes.number,
};

SidebarSection.Item = SidebarSectionItem;
SidebarSection.Skeleton = SidebarSectionSkeleton;

export default SidebarSection;

